// src/components/admin/flows/canvas/useCanvasZoom.js
import { useState, useCallback, useRef } from 'react';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2.0;
const ZOOM_STEP = 0.1;

const useCanvasZoom = () => {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const isPanning = useRef(false);
    const panStart = useRef({ x: 0, y: 0 });
    const panOrigin = useRef({ x: 0, y: 0 });

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        setZoom(prev => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev + delta)));
    }, []);

    const zoomIn = useCallback(() => {
        setZoom(prev => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
    }, []);

    const zoomOut = useCallback(() => {
        setZoom(prev => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
    }, []);

    const fitToScreen = useCallback(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, []);

    const startPan = useCallback((e) => {
        // Middle mouse button or spacebar+click
        if (e.button === 1 || e.shiftKey) {
            e.preventDefault();
            isPanning.current = true;
            panStart.current = { x: e.clientX, y: e.clientY };
            panOrigin.current = { ...pan };
        }
    }, [pan]);

    const updatePan = useCallback((e) => {
        if (!isPanning.current) return;
        const dx = e.clientX - panStart.current.x;
        const dy = e.clientY - panStart.current.y;
        setPan({
            x: panOrigin.current.x + dx,
            y: panOrigin.current.y + dy,
        });
    }, []);

    const endPan = useCallback(() => {
        isPanning.current = false;
    }, []);

    return {
        zoom,
        pan,
        isPanning: isPanning.current,
        handleWheel,
        zoomIn,
        zoomOut,
        fitToScreen,
        startPan,
        updatePan,
        endPan,
        setZoom,
        setPan,
    };
};

export default useCanvasZoom;
